
import { getSecureItem, removeSecureItem, setSecureJSON } from '@/utils/localStorage';

export interface TimeTracking {
  hours: number;
  minutes: number;
  seconds: number;
}

export interface BreakRecord {
  startTime: Date;
  endTime?: Date;
  duration: TimeTracking;
  reason?: string;
  approved?: boolean;
}

export interface DailyTimeRecord {
  date: string;
  checkInTime: string;
  checkOutTime?: string;
  totalWorkTime: TimeTracking;
  productiveTime: TimeTracking;
  totalBreakTime: TimeTracking;
  breaks: BreakRecord[];
  notes?: string;
  efficiency: number;
}

export interface WeeklyReport {
  weekStartDate: string;
  totalWorkHours: number;
  averageEfficiency: number;
  totalBreakTime: number;
  mostProductiveDay: string;
}

class TimeTrackingService {
  private readonly STORAGE_KEY_PREFIX = 'timeTracking';
  private readonly HISTORY_KEY_PREFIX = 'timeTrackingHistory';
  private readonly MAX_HISTORY_DAYS = 30;

  // Get user-specific storage key
  private getStorageKey(userId?: string): string {
    if (userId) {
      return `${this.STORAGE_KEY_PREFIX}_${userId}`;
    }
    return this.STORAGE_KEY_PREFIX;
  }

  // Get user-specific history key
  private getHistoryKey(userId?: string): string {
    if (userId) {
      return `${this.HISTORY_KEY_PREFIX}_${userId}`;
    }
    return this.HISTORY_KEY_PREFIX;
  }

  // Get current tracking state with decryption (user-specific)
  public getCurrentState(userId?: string) {
    const key = this.getStorageKey(userId);
    const encryptedData = getSecureItem(key);
    if (!encryptedData) return null;

    try {
      const data = JSON.parse(encryptedData);
      // Verify the stored data belongs to the current user
      if (userId && data.userId && data.userId !== userId) {
        return null; // Data belongs to a different user
      }
      return data;
    } catch (error) {
      console.error('Error reading time tracking state:', error);
      return null;
    }
  }

  // Save current state with encryption (user-specific)
  public saveCurrentState(data: any, userId?: string) {
    try {
      const key = this.getStorageKey(userId);
      const dataToSave = userId ? { ...data, userId } : data;
      setSecureJSON(key, dataToSave);
    } catch (error) {
      console.error('Error saving time tracking state:', error);
    }
  }

  // Clear state for a specific user
  public clearUserState(userId?: string) {
    const key = this.getStorageKey(userId);
    const historyKey = this.getHistoryKey(userId);
    removeSecureItem(key);
    removeSecureItem(historyKey);
  }

  // Save daily record on checkout (user-specific)
  public saveDailyRecord(record: DailyTimeRecord, userId?: string) {
    try {
      const history = this.getTimeHistory(userId);
      history.unshift(record);

      // Keep only last 30 days
      if (history.length > this.MAX_HISTORY_DAYS) {
        history.pop();
      }

      const historyKey = this.getHistoryKey(userId);
      setSecureJSON(historyKey, history);
    } catch (error) {
      console.error('Error saving daily record:', error);
    }
  }

  // Get time tracking history (user-specific)
  public getTimeHistory(userId?: string): DailyTimeRecord[] {
    try {
      const historyKey = this.getHistoryKey(userId);
      const encryptedHistory = getSecureItem(historyKey);
      if (!encryptedHistory) return [];

      return JSON.parse(encryptedHistory);
    } catch (error) {
      console.error('Error reading time history:', error);
      return [];
    }
  }

  // Generate weekly report
  public generateWeeklyReport(startDate: Date): WeeklyReport {
    const history = this.getTimeHistory();
    const weekRecords = history.filter(record => {
      const recordDate = new Date(record.date);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);
      return recordDate >= startDate && recordDate < endDate;
    });

    if (weekRecords.length === 0) {
      return {
        weekStartDate: startDate.toISOString(),
        totalWorkHours: 0,
        averageEfficiency: 0,
        totalBreakTime: 0,
        mostProductiveDay: '-'
      };
    }

    const totalWorkHours = weekRecords.reduce((total, record) => {
      return total + (record.totalWorkTime.hours + record.totalWorkTime.minutes / 60);
    }, 0);

    const averageEfficiency = weekRecords.reduce((total, record) => {
      return total + record.efficiency;
    }, 0) / weekRecords.length;

    const totalBreakTime = weekRecords.reduce((total, record) => {
      return total + (record.totalBreakTime.hours + record.totalBreakTime.minutes / 60);
    }, 0);

    const mostProductiveDay = weekRecords.reduce((most, record) => {
      return record.efficiency > most.efficiency ? record : most;
    }).date;

    return {
      weekStartDate: startDate.toISOString(),
      totalWorkHours: Math.round(totalWorkHours * 100) / 100,
      averageEfficiency: Math.round(averageEfficiency),
      totalBreakTime: Math.round(totalBreakTime * 100) / 100,
      mostProductiveDay
    };
  }

  // Format time for display
  public formatTime(time: TimeTracking): string {
    return `${time.hours.toString().padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}:${time.seconds.toString().padStart(2, '0')}`;
  }

  // Calculate time difference in minutes
  public calculateTimeDifference(start: Date, end: Date): number {
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  }

  // Validate break duration
  public validateBreakDuration(duration: number): boolean {
    const MAX_BREAK_DURATION = 60; // 60 minutes
    return duration <= MAX_BREAK_DURATION;
  }

  // Clear all data (for logout) - user-specific
  public clearData(userId?: string) {
    this.clearUserState(userId);
  }

  // Clear all users' data (for cleanup)
  public clearAllData() {
    // This would require iterating through all possible user IDs
    // For now, we'll just clear the default keys
    removeSecureItem(this.STORAGE_KEY_PREFIX);
    removeSecureItem(this.HISTORY_KEY_PREFIX);
  }
}

export const timeTrackingService = new TimeTrackingService(); 